import { useEffect, useState } from "react";
import { toast } from "sonner";
import { MessageSquareHeart, Loader2, Ban } from "lucide-react";
import { http, apiError, formatDateTime } from "../../lib/api";
import { AutoStatusPill } from "./reviewAutoUi";

const LABEL = { sent: ["Sent", "text-emerald-300"], submitted: ["Review received", "text-gold"], failed: ["Failed to send", "text-rose-300"], sending: ["Not confirmed", "text-slate-400"] };
const KIND = { initial: "Automatic request", reminder: "Automatic reminder", manual: "Manual request" };

const AutoState = ({ auto, enabled, onCancel, busy }) => (
  <div className="mt-3 border hairline p-3 text-xs text-slate-300 space-y-1" data-testid="review-auto-state">
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-slate-400">Automatic request:</span>
      <AutoStatusPill status={auto.status} testid="review-auto-status" />
      {["scheduled", "sent"].includes(auto.status) && (
        <button className="btn-ghost !py-1 !px-2 !text-[0.65rem] ml-auto" onClick={onCancel} disabled={busy} data-testid="review-auto-cancel-button"><Ban className="w-3 h-3" /> {auto.status === "scheduled" ? "Cancel request" : "Cancel reminder"}</button>
      )}
    </div>
    {auto.status === "scheduled" && <p data-testid="review-auto-scheduled-for">Scheduled for {formatDateTime(auto.scheduled_for)}{!enabled ? " (automation is turned off, so it will not send)" : ""}</p>}
    {auto.initial_sent_at && <p data-testid="review-auto-initial-at">Initial request sent {formatDateTime(auto.initial_sent_at)}</p>}
    {auto.status === "sent" && auto.reminder_due && <p>Reminder due {formatDateTime(auto.reminder_due)} if no review is received</p>}
    {auto.reminder_sent_at && <p data-testid="review-auto-reminder-at">Reminder sent {formatDateTime(auto.reminder_sent_at)}</p>}
    {auto.status === "canceled" && <p data-testid="review-auto-cancel-reason">{auto.cancel_reason}{auto.canceled_by ? ` · ${auto.canceled_by}` : ""}</p>}
    {auto.last_error && auto.status === "scheduled" && <p className="text-rose-300">Last attempt failed; will retry automatically.</p>}
  </div>
);

export const RequestReview = ({ item }) => {
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => http.get(`/admin/inquiries/${item.id}/review-requests`).then(({ data }) => setInfo(data)).catch(() => setInfo(null));
  useEffect(() => { load(); }, [item.id, item.status]); // eslint-disable-line react-hooks/exhaustive-deps
  const items = Array.isArray(info?.items) ? info.items : [];
  const completed = item.status === "completed";
  const submitted = items.some((r) => r.status === "submitted");
  const act = async (fn, msg) => {
    setBusy(true);
    try {
      await fn();
      toast.success(msg);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
      load();
    }
  };
  const send = () => act(() => http.post(`/admin/inquiries/${item.id}/review-request`), `Review request emailed to ${item.email}`);
  const cancel = () => act(() => http.post(`/admin/inquiries/${item.id}/review-request/cancel`), "Automatic review request canceled");
  return (
    <section data-testid="request-review">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="eyebrow">Request a Review</p>
        <button className="btn-gold !py-2.5 !px-4" onClick={send} disabled={!completed || submitted || busy || !item.email || info?.opted_out} data-testid="request-review-button">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <MessageSquareHeart className="w-4 h-4" />} {items.some((r) => r.status === "sent") ? "Send again" : "Request a Review"}
        </button>
      </div>
      <p className="text-xs text-slate-400 mt-2" data-testid="request-review-hint">
        {submitted ? "This customer has already submitted a review. Find it under Client reviews, pending your approval."
          : info?.opted_out ? "This customer has opted out of review request emails."
          : completed ? `Emails ${item.email} a personal, single-use link to review this inquiry. Submitted reviews wait for approval before they appear.`
          : "Available once the inquiry status is Completed. An automatic request is scheduled 3 days after completion."}
      </p>
      {info?.auto && <AutoState auto={info.auto} enabled={info.automation_enabled} onCancel={cancel} busy={busy} />}
      {items.length > 0 && (
        <ul className="mt-3 divide-y divide-white/10 border-y hairline" data-testid="request-review-history">
          {items.map((r) => {
            const [label, cls] = LABEL[r.status] || [r.status, "text-slate-400"];
            return (
              <li key={r.id} className="py-2 text-xs text-slate-300" data-testid={`request-review-row-${r.id}`}>
                {KIND[r.kind] || "Manual request"} · <span className={cls}>{label}</span> · {formatDateTime(r.sent_at || r.created_at)} · by {r.sent_by}{r.revoked_at && r.status === "sent" ? " · replaced by newer link" : ""}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
};
